import { NetworkStatus } from "@apollo/client";
import { Query } from "@apollo/client/react/components";
import { SectionJumpIdentifiers } from "components/company/SectionJumpIdentifiers";
import FeatureBox from "components/conversion/FeatureBox";
import OnlyPremiumRender from "components/conversion/OnlyPremiumRender";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import { SpanPixelBeacon } from "components/ui/ScrollJump";
import Twemoji from "components/ui/Twemoji";
import classNames from "components/ui/classNames";
import { LinkButton } from "lib/miolo";
import { FormattedMessage } from "react-intl";
import DiscussionCommentsByAsset from "../../lib/graphql/queries/discussions/DiscussionCommentsByAsset";
import type {
  IDiscussionCommentsByAssetQuery,
  IDiscussionCommentsByAssetQueryVariables,
} from "../../lib/graphql/queries/discussions/__generated__/DiscussionCommentsByAsset.generated";
import useSelfie from "../../lib/hooks/useSelfie";
import type { IAssetType } from "../../types/graphqlTypes";
import { IAssetDiscussionCommentOrderField, IOrderDirection } from "../../types/graphqlTypes";
import AssetDiscussionCommentsList from "./AssetDiscussionCommentsList";
import DiscussionsContainer from "./DiscussionsContainer";
import MustBePremiumToSeeMoreNotice from "./MustBePremiumToSeeMoreNotice";
import NoCommentsBlankslate from "./NoCommentsBlankslate";
import WholeDiscussionHeaderLineWithCounter from "./WholeDiscussionHeaderLineWithCounter";
import RootCommentInputForm from "./form/RootCommentInputForm";
import PostCommentOnAssetMutation from "./mutations/PostCommentOnAssetMutation";

interface AssetDiscussionProps {
  assetType: IAssetType;
  assetId: string;
  hideHeaderLine?: boolean;
}

export default function AssetDiscussion({ assetType, assetId, hideHeaderLine = false }: AssetDiscussionProps) {
  const selfie = useSelfie();

  if (!selfie) {
    return null;
  }

  const postComment = (
    <PostCommentOnAssetMutation>
      {(postCommentOnAsset) => (
        <RootCommentInputForm
          viewer={selfie}
          onSubmit={async (bodyAsPlainText) => {
            return postCommentOnAsset({
              assetType,
              assetId,
              bodyAsPlainText,
            });
          }}
        />
      )}
    </PostCommentOnAssetMutation>
  );

  return (
    <Query<IDiscussionCommentsByAssetQuery, IDiscussionCommentsByAssetQueryVariables>
      query={DiscussionCommentsByAsset}
      notifyOnNetworkStatusChange={true}
      variables={{
        assetId,
        cursor: null,
        limit: 30,
        orderBy: {
          field: IAssetDiscussionCommentOrderField.PostedAt,
          direction: IOrderDirection.Descending,
        },
      }}
    >
      {({ data, loading, networkStatus, fetchMore }) => {
        if (loading && networkStatus !== NetworkStatus.fetchMore && networkStatus !== NetworkStatus.refetch) {
          return <LoadingSkeleton count={1} className="min-h-96 xl:h-full" />;
        }

        if (!data) {
          return null;
        }

        const { discussionCommentsByAsset } = data;

        if (!discussionCommentsByAsset) {
          return null;
        }

        return (
          <div className="w-full">
            {!selfie.isPremium && (
              <div
                className={classNames(
                  "flex flex-col items-center justify-center gap-y-6 rounded-xl bg-indigo-50/50 p-6",
                  "ring-1 ring-indigo-200 ring-offset-2 ring-offset-white",
                  "dark:bg-indigo-600/5 dark:ring-slate-900 dark:ring-offset-0 dark:ring-offset-slate-900",
                )}
              >
                <h2 className="space-x-2 rounded-xl px-2 py-1 text-sm font-bold italic text-slate-700 dark:bg-slate-950/40 dark:px-4 dark:py-2 dark:text-slate-300">
                  <span>
                    <FormattedMessage
                      defaultMessage="{commentsCount} comentários..."
                      id="DPvGpv"
                      values={{
                        commentsCount: discussionCommentsByAsset.totalCount,
                      }}
                    />
                  </span>
                  <Twemoji emoji="🔥" />
                </h2>

                <div>
                  <FeatureBox
                    displayOrigin="Discussions"
                    buttonText="Participar da comunidade agora"
                    callToActionText={
                      <>
                        Turbine suas análises com <strong>dados exclusivos</strong> de mais de 6.000 ativos.
                      </>
                    }
                    featureHighlights={[
                      "Acesso ao Ranking completo",
                      "Agenda de Dividendos",
                      "Central de Resultados",
                      "Histórico máximo de dados",
                    ]}
                  />
                </div>
              </div>
            )}

            <OnlyPremiumRender>
              <div className="relative space-y-4">
                {!loading && <SpanPixelBeacon offset={-200} id={SectionJumpIdentifiers.Discussions} />}

                {!hideHeaderLine && (
                  <WholeDiscussionHeaderLineWithCounter
                    totalCount={discussionCommentsByAsset.totalCount}
                    totalCountOf="COMMENTS"
                  />
                )}

                <DiscussionsContainer>
                  {discussionCommentsByAsset.nodes.length === 0 && (
                    <NoCommentsBlankslate viewerCanComment={selfie.isPremium} />
                  )}

                  {selfie.isPremium && postComment}

                  {discussionCommentsByAsset.nodes.length > 0 && (
                    <div className="p-6">
                      <AssetDiscussionCommentsList comments={discussionCommentsByAsset.nodes} />
                    </div>
                  )}

                  {discussionCommentsByAsset.pageInfo.hasNextPage && selfie.isPremium && (
                    <div className="flex justify-center p-6 font-normal">
                      <LinkButton
                        disabled={loading && networkStatus === NetworkStatus.fetchMore}
                        variant={loading && networkStatus === NetworkStatus.fetchMore ? "disabled" : "primary"}
                        onClick={() => {
                          fetchMore({
                            variables: {
                              cursor: discussionCommentsByAsset.pageInfo.cursor,
                            },
                            updateQuery: (previousQueryResult, { fetchMoreResult }) => {
                              if (!fetchMoreResult) {
                                return previousQueryResult;
                              }

                              return {
                                ...previousQueryResult,
                                ...fetchMoreResult,
                                discussionCommentsByAsset: {
                                  ...previousQueryResult.discussionCommentsByAsset,
                                  ...fetchMoreResult.discussionCommentsByAsset,
                                  nodes: previousQueryResult.discussionCommentsByAsset.nodes.concat(
                                    fetchMoreResult.discussionCommentsByAsset.nodes,
                                  ),
                                },
                              };
                            },
                          });
                        }}
                      >
                        {loading && networkStatus === NetworkStatus.fetchMore
                          ? "Carregando mais comentários..."
                          : "Ver comentários mais antigos"}
                      </LinkButton>
                    </div>
                  )}

                  {discussionCommentsByAsset.pageInfo.hasNextPage && !selfie.isPremium && (
                    <MustBePremiumToSeeMoreNotice totalOfComments={discussionCommentsByAsset.totalCount} />
                  )}

                  {discussionCommentsByAsset.nodes.length > 10 && selfie.isPremium && postComment}
                </DiscussionsContainer>
              </div>
            </OnlyPremiumRender>
          </div>
        );
      }}
    </Query>
  );
}
